import React from 'react'
import Imgix from 'react-imgix'
import { useMeta, useDontLazyload } from '@uswitch/ev-utils/context/selectors'
import { useImageOptimisation } from './use-image-optimisation'
import { ConditionalWrapper } from '@uswitch/ev-utils/conditional-wrapper'

export const cf2imgix = (cfUrl = '', params = {}) => {
  const url = global.decodeURIComponent(
    cfUrl
      .replace(/(https?:)?\/\/(downloads|images).ctfassets.net\//g, 'https://img.uswitch.com/')
      // imgix expects unencoded string for URL and treats + as encoded space
      .replace(/\+/g, ' '),
  )

  return url ? `${url}?${new URLSearchParams(params).toString()}` : ''
}

const pxToInt = input => parseInt(String(input).replace('px', ''))

const DEFAULT_DEVICE_PIXEL_RATIOS = [1, 2]
const DEFAULT_WIDTH_TOLERANCE = 0.2
const DEFAULT_MIN_WIDTH = 100
const DEFAULT_UPPER_LIMIT_VIEWPORT = 2800
const DEFAULT_QUALITY = 45

const ImgixImage = ({
  src,
  width,
  height,
  sizes,
  alt,
  className = '',
  itemProp,
  critical = false,
  sx,
  imgixParams = {},
  ctfToUswitchContenftul = true,
  clearPx = true,
  imgixAutoFormatCompress = true,
  svgAsImg = true,
  aspectRatio,
  aspectRatioProps,
  ...props
}) => {
  const {
    devicePixelRatios = DEFAULT_DEVICE_PIXEL_RATIOS,
    widthTolerance = DEFAULT_WIDTH_TOLERANCE,
    minWidth = DEFAULT_MIN_WIDTH,
    quality = DEFAULT_QUALITY,
  } = useMeta(['imgixImage'], {})

  const disableLazyLoad = useDontLazyload()

  const { imageSizes, maxWidth } = useImageOptimisation({ width, height, sizes, devicePixelRatios })
  const forceDisableLazyLoad = process.env.RAZZLE_DISABLE_LAZYLOAD === 'true' || disableLazyLoad
  const doLazyLoad = !forceDisableLazyLoad && !critical

  const srcSetOptions = {
    devicePixelRatios,
    widthTolerance,
    minWidth,
    maxWidth: isNaN(maxWidth)
      ? DEFAULT_UPPER_LIMIT_VIEWPORT * Math.max(...devicePixelRatios)
      : maxWidth,
  }

  if (!src) {
    return (
      <div
        className={className}
        sx={{
          backgroundColor: 'light-2',
          display: 'inline-block',
          width,
          height,
        }}
      ></div>
    )
  }

  if (svgAsImg && src.endsWith('.svg')) {
    if (doLazyLoad && process.env.NODE_ENV === 'development') {
      console.warn("Lazy loading isn't supported with SVG images")
    }

    return (
      <img
        src={cf2imgix(src)}
        alt={alt}
        className={className}
        height={height && pxToInt(height)}
        width={width && pxToInt(width)}
        {...{ ...props, dispatch: undefined }}
      />
    )
  }

  const finalImgixParams = imgixAutoFormatCompress
    ? {
        auto: 'format,compress',
        ...imgixParams,
        q: quality,
      }
    : { ...imgixParams, q: quality }

  return (
    <ConditionalWrapper
      condition={aspectRatio && !(height && width)}
      aspectRatio={aspectRatio}
      wrapper={AspectRatioWrapper}
      aspectRatioProps={aspectRatioProps}
    >
      <Imgix
        htmlAttributes={{ alt, itemProp }}
        attributeConfig={
          doLazyLoad && {
            src: 'data-src',
            srcSet: 'data-srcset',
            sizes: 'data-sizes',
          }
        }
        className={`${doLazyLoad ? 'lazyload ' : ''}${className}`}
        src={ctfToUswitchContenftul ? cf2imgix(src) : src}
        height={clearPx ? pxToInt(height) : height}
        width={clearPx ? pxToInt(width) : width}
        sx={{ display: 'block', width: width || '100%', ...sx }}
        sizes={imageSizes}
        imgixParams={finalImgixParams}
        srcSetOptions={srcSetOptions}
        {...props}
      />
    </ConditionalWrapper>
  )
}

const AspectRatioWrapper = ({ children, aspectRatio, aspectRatioProps: rest }) => (
  <div
    className='AspectRatioWrapper'
    sx={{
      position: 'relative',
      width: '100%',
      paddingBottom: `${aspectRatio * 100}%`,
      img: { position: 'absolute', top: 0, right: 0, left: 0, bottom: 0 },
    }}
    {...rest}
  >
    {children}
  </div>
)

export default React.memo(ImgixImage)
